import React, { useEffect, useRef, useState } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { slurpApi } from "../../lib/slurpApi";
import ProfileFormModal from "./ProfileFormModal";

/**
 * Mounted globally inside <App>. Watches the connected wallet — when a
 * wallet connects for the first time and has no profile + hasn't yet
 * dismissed the prompt on this device, opens WelcomeProfileModal.
 *
 * Skip behavior is per-wallet, persisted in localStorage. Server is the
 * source of truth for whether the profile exists; localStorage just
 * remembers "user said not now" so we don't nag every page load.
 */
export default function ProfileGuard() {
  const wallet = useWallet();
  const [openWallet, setOpenWallet] = useState(null);
  const checkedRef = useRef(new Set()); // wallets we've already checked this session

  useEffect(() => {
    const w = wallet.publicKey?.toBase58();
    if (!w) return;
    if (checkedRef.current.has(w)) return;
    checkedRef.current.add(w);

    // Skipped on this device? Don't prompt.
    try {
      if (localStorage.getItem(`slurp:profile_skipped:${w}`) === "1") return;
    } catch { /* private mode, treat as not-skipped */ }

    let cancelled = false;
    (async () => {
      try {
        await slurpApi.getProfile(w);
        // 200 — wallet has a profile already, do nothing.
      } catch (err) {
        if (cancelled) return;
        const msg = err?.message || "";
        // Only show the welcome modal on a real 404. Network errors etc.
        // shouldn't trigger an annoying first-connect prompt.
        if (msg.includes("404") || msg.includes("no_profile")) {
          setOpenWallet(w);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [wallet.publicKey]);

  // Wallet disconnects mid-modal? Close the modal.
  useEffect(() => {
    const current = wallet.publicKey?.toBase58();
    if (openWallet && openWallet !== current) {
      setOpenWallet(null);
    }
  }, [wallet.publicKey, openWallet]);

  if (!openWallet) return null;
  // Treat any close-without-save (X button, backdrop click, Escape) as
  // "skip for now" — same effect as clicking the explicit Skip button.
  // Without this, a user dismissing via X gets re-prompted on every page
  // load until they either create a profile or find the Skip button. The
  // flag is per-wallet, so a different wallet still sees the prompt.
  const dismiss = () => {
    try { localStorage.setItem(`slurp:profile_skipped:${openWallet}`, "1"); }
    catch { /* private mode */ }
    setOpenWallet(null);
  };
  return (
    <ProfileFormModal
      mode="welcome"
      initial={null}
      onClose={dismiss}
      onSaved={() => setOpenWallet(null)}
    />
  );
}
